import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HlmInputDirective } from '@spartan-ng/helm/input';

/**
 * Multi-line counterpart to {@link SuffixInput} — same label, required marker and
 * help/error treatment, so a description field lines up with the inputs above it.
 * Chrome comes from `hlmInput`, so it tracks the shared field design automatically.
 *
 * @example
 * <app-textarea-field label="Beschreibung" placeholder="Wofür ist diese Vorlage gedacht?"
 *                     formControlName="description" />
 */
@Component({
  selector: 'app-textarea-field',
  standalone: true,
  imports: [HlmInputDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaField),
      multi: true,
    },
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label class="text-sm font-normal text-foreground">
          {{ label() }}
          @if (required()) {
            <span class="text-destructive ml-0.5">*</span>
          }
        </label>
      }
      <textarea
        hlmInput
        [rows]="rows()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onTouched()"
        class="resize-none"
      ></textarea>
      @if (helpText() && !errorText()) {
        <p class="text-xs text-muted-foreground">{{ helpText() }}</p>
      }
      @if (errorText()) {
        <p class="text-xs text-destructive">{{ errorText() }}</p>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaField implements ControlValueAccessor {
  label = input<string | undefined>(undefined);
  placeholder = input<string>('');
  required = input(false, { transform: booleanAttribute });
  rows = input<number>(3);
  helpText = input<string | undefined>(undefined);
  errorText = input<string | null | undefined>(undefined);

  protected readonly value = signal<string>('');
  protected readonly disabled = signal(false);

  private onChange: (value: string) => void = () => {};
  protected onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLTextAreaElement).value;
    this.value.set(next);
    this.onChange(next);
  }
}
